import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  LabelList,
  Customized,
} from "recharts";
import { formatWithCommas } from "../../../../utils/formatter";
import { Whisper, Tooltip as RsuiteTooltip } from "rsuite";
import ProductInfoIcon from "../../../../assets/info-circle-grey.svg";

type Props = {
  commodity: string;
  data: Record<string, any>[];
  name: string;
  vendors: string[];
  partnerVendor: string;
  unit: string;
  decimal: number;
  isdownload: boolean;
  tmData?: { month: string; tmPercentage: string }[];
  trendView?: string;
  othersVendors?: string[];
};

const StackBarGraph = ({
  commodity,
  data,
  name,
  unit = "",
  vendors = [],
  partnerVendor = "",
  decimal = 0,
  isdownload = false,
  tmData = [],
  trendView = "",
  othersVendors = [],
}: Props) => {
  // Normalize a month string (e.g. "Jan-2026" or "Jan-26") to "Jan-26"
  const normalizeMonth = (m: string) => {
    if (!m) return "";
    const [mon, yr] = m.split("-");
    return `${mon}-${(yr ?? "").slice(-2)}`;
  };

  // Map of normalized month -> TM percentage for quick lookup on each tick
  const tmMap: Record<string, string> = {};
  (tmData || []).forEach((item) => {
    if (item?.month != null) {
      tmMap[normalizeMonth(item.month)] = item.tmPercentage;
    }
  });

  const hasTmData = (tmData || []).length > 0;

  const isWeekly = (trendView || "").toLowerCase() === "weekly";

  // Strip the trailing "-YY" year (e.g. "Aug 04-26" -> "Aug 04") for weekly axis labels
  const stripYear = (m: string) =>
    m && m.lastIndexOf("-") > 0 ? m.substring(0, m.lastIndexOf("-")) : m;

  // In weekly view, show only the most recent 12 bars in this (left) chart
  const chartData = isWeekly ? (data || []).slice(-12) : data;

  const TM_AXIS_HEIGHT = 42;

  // Custom x-axis tick that renders the month and its TM% just below it
  const renderMonthTick = (props: any) => {
    const { x, y, payload } = props;
    const tm = tmMap[normalizeMonth(payload?.value)];
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={14}
          textAnchor="middle"
          fontSize={11}
          fill="#333"
        >
          {isWeekly ? stripYear(payload?.value) : payload?.value}
        </text>
        {hasTmData && (
          <text
            x={0}
            y={0}
            dy={30}
            textAnchor="middle"
            fontSize={11}
            fontWeight="bold"
            fill="#000000"
          >
            {tm != null ? `${Number(tm).toFixed(1)}%` : "-"}
          </text>
        )}
      </g>
    );
  };
  const vendorGraphTheme = {
    ATKORE: "#62b5e5",
    ZEKELMAN: "#012169",
    NUCOR: "#046A38",
    IPEX: "#9c765f",
    "PRIME CONDUIT": "#8cd17e",
    CANTEX: "#0097a9",
    "NATIONAL PIPE": "#FF921D",
    "SOUTHERN PIPE": "#b17aa1",
    "JM EAGLE": "#6A3D9A",
    "Other Suppliers": "#bcbcbc",
    Others: "#bcbcbc",
    OTHERS: "#bcbcbc",
  };

  const wireVendorGraphTheme = {
    "ALAN WIRE": "#a0dbfe",
    CERROWIRE: "#e97132",
    "CME WIRE": "#da291c",
    "COLONIAL WIRE": "#007cb0",
    ENCORE: "#50b539",
    "REPUBLIC WIRE": "#9c765f",
    "SERVICE WIRE": "#041e42",
    SOUTHWIRE: "#a02b93",
    OMNICABLE: "#203c74",
    "PRIORITY WIRE": "#ffc000",
    "Other Suppliers": "#bcbcbc",
    Others: "#bcbcbc",
    OTHERS: "#bcbcbc",
  };

  const sortedVendors =
    partnerVendor !== "" ? [vendors[1], vendors[0]] : vendors;
  return (
    <div className="shadow-module-card bg-white w-full h-full">
      <div className="font-bold pl-[1.2vh] pt-[1.2vh] pb-[0.5vh] text-[1.9vh]">
        <>
          <span>{name}</span>
          {partnerVendor !== "" && (
            <span>
              {" "}
              for <span className="text-[#86BC25]">{partnerVendor}</span>
            </span>
          )}
        </>
      </div>
      <div className="flex flex-wrap items-start h-10">
        {vendors.map((ele, i) => (
          <div key={i} className={`flex items-center ml-[2vh]`}>
            <div
              style={{
                backgroundColor:
                  commodity === "Copper Wire"
                    ? wireVendorGraphTheme[
                        ele as keyof typeof wireVendorGraphTheme
                      ]
                    : vendorGraphTheme[ele as keyof typeof vendorGraphTheme],
              }}
              className={`lengends h-[0.5vh] w-[1vw] rounded-[1vh] mr-[0.5vh]`}
            ></div>
            <div className="text-[1.4vh]">{ele}</div>
            {ele.trim().toLowerCase() === "others" && (
              <Whisper
                placement="top"
                speaker={
                  <RsuiteTooltip>
                    <div className="text-xs text-left">
                      {othersVendors.length > 0 ? (
                        <ul className="list-disc pl-3">
                          {othersVendors.map((vendor) => (
                            <li key={vendor}>{vendor}</li>
                          ))}
                        </ul>
                      ) : (
                        <div>No vendors available</div>
                      )}
                    </div>
                  </RsuiteTooltip>
                }
              >
                <img
                  src={ProductInfoIcon}
                  alt="info"
                  className="w-[1.5vh] cursor-pointer inline shrink-0 ml-[0.5vh]"
                />
              </Whisper>
            )}
          </div>
        ))}
      </div>
      {/* <div className="flex pt-1">
        {vendors.slice(vendors.length / 2).map((ele, i) => (
          <div key={i} className={`flex items-center ml-[2vh]`}>
            <div
              style={{
                backgroundColor:
                  commodity === "Copper Wire"
                    ? wireVendorGraphTheme[
                        ele as keyof typeof wireVendorGraphTheme
                      ]
                    : vendorGraphTheme[ele as keyof typeof vendorGraphTheme],
              }}
              className={`lengends h-[0.5vh] w-[1vw] rounded-[1vh] mr-[0.5vh]`}
            ></div>
            <div className="text-[1.4vh]">{ele}</div>
          </div>
        ))}
      </div> */}
      <div className="h-[85%] w-full">
        <ResponsiveContainer height="92%">
          <BarChart style={{ padding: "1vh 1vh 1vh 0vh" }} data={chartData}>
            {/* <CartesianGrid /> */}
            <XAxis
              dataKey="month"
              interval={0}
              height={hasTmData ? TM_AXIS_HEIGHT : 30}
              tick={renderMonthTick}
            />
            <YAxis
              domain={[0, 100]}
              tickFormatter={(value) => {
                return value + unit;
              }}
              hide={isdownload}
            />
            <Tooltip
              formatter={(value: any, name: any, props: any) => {
                if (value) {
                  return [
                    `${parseFloat(value)?.toFixed(decimal).toString()}%`,
                    props.dataKey,
                  ];
                } else {
                  return [];
                }
              }}
            />
            {sortedVendors.map((ele, i) => (
              <Bar
                key={i}
                dataKey={ele}
                stackId="a"
                fill={
                  commodity === "Copper Wire"
                    ? wireVendorGraphTheme[
                        ele as keyof typeof wireVendorGraphTheme
                      ]
                    : vendorGraphTheme[ele as keyof typeof vendorGraphTheme]
                }
                activeBar={{ stroke: `black`, strokeWidth: 1 }}
              >
                <LabelList
                  dataKey={ele}
                  position="insideTop"
                  fill="white"
                  fontSize={11}
                  formatter={(value: any) => {
                    return isdownload && commodity === "Steel Conduit"
                      ? Number(parseFloat(value)?.toFixed(decimal)) > 5
                        ? `${parseFloat(value)?.toFixed(decimal).toString()}%`
                        : ""
                      : "";
                  }}
                />
              </Bar>
            ))}
            {hasTmData && (
              <Customized
                component={(props: any) => {
                  const off = props?.offset;
                  if (!off) return null;
                  const axisY = off.top + off.height;
                  const yPos = axisY + 38;
                  const count = (chartData || []).length;
                  const dividerTop = axisY;
                  const dividerBottom = axisY + TM_AXIS_HEIGHT;
                  const horizontalLineY = axisY + 27;
                  const dividers = [];
                  for (let i = 1; i < count; i++) {
                    const x = off.left + (off.width * i) / count;
                    dividers.push(
                      <line
                        key={`tm-divider-${i}`}
                        x1={x}
                        y1={dividerTop}
                        x2={x}
                        y2={dividerBottom}
                        stroke="#d9d9d9"
                        strokeWidth={1}
                      />
                    );
                  }
                  return (
                    <g>
                      {dividers}
                      <line
                        x1={off.left}
                        x2={off.left + off.width}
                        y1={horizontalLineY}
                        y2={horizontalLineY}
                        stroke="#d9d9d9"
                        strokeWidth={1}
                      />
                      <text
                        x={6}
                        y={yPos}
                        fontSize={11}
                        fontWeight={700}
                        fill="#000000"
                      >
                        TM %
                      </text>
                    </g>
                  );
                }}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default StackBarGraph;
